import React, { useState, useRef, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ChevronRight, BookOpen, Menu, X, Crown, Briefcase, BadgeDollarSign, Library, Rocket, ArrowLeft } from "lucide-react";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import "./styles/tools_walkthrough.css";

const TIERS = {
  free: { label: "Free", accent: "#B4B2A9", dot: "#888780" },
  pro: { label: "Professional", accent: "#5DCAA5", dot: "#1D9E75" },
  elite: { label: "Elite", accent: "#EF9F27", dot: "#BA7517" },
};

const SECTIONS = [
  { id: "start", label: "Start here", items: [
    { id: "overview", label: "Platform overview" },
    { id: "layers", label: "The three layers" },
    { id: "controls", label: "Chart controls" },
    { id: "routines", label: "Pre-market routines" },
  ]},
  { id: "elite", label: "Elite", tier: "elite", items: [
    { id: "orderflow", label: "Order Flow", tier: "elite" },
    { id: "orderflow-replay", label: "Order Flow Replay", tier: "elite" },
    { id: "smart-candlesticks", label: "Smart Candlesticks™", tier: "elite" },
    { id: "gamma-density", label: "Gamma Density", tier: "elite" },
    { id: "gamma-exposure", label: "Gamma Exposure", tier: "elite" },
    { id: "global-bookmarks", label: "Global Bookmarks", tier: "elite" },
  ]},
  { id: "pro", label: "Professional", tier: "pro", items: [
    { id: "advanced-mp", label: "Advanced Market Profile", tier: "pro" },
    { id: "spectrum", label: "Spectrum", tier: "pro" },
    { id: "ntm-volx", label: "NTM VolX™", tier: "pro" },
    { id: "ntm-oi", label: "NTM OI", tier: "pro" },
    { id: "wcash", label: "WCash™", tier: "pro" },
    { id: "straddles", label: "Straddles", tier: "pro" },
    { id: "mp-bookmarks", label: "MP Bookmarks", tier: "pro" },
  ]},
  { id: "free", label: "Free Access", tier: "free", items: [
    { id: "options-table", label: "Options Table", tier: "free" },
    { id: "payoff-chart", label: "Payoff Chart", tier: "free" },
    { id: "option-greeks", label: "Option Greeks", tier: "free" },
    { id: "iv-smile", label: "IV Smile", tier: "free" },
    { id: "oi-viz", label: "OI Viz", tier: "free" },
    { id: "total-oi", label: "Total OI", tier: "free" },
    { id: "total-volumes", label: "Total Volumes", tier: "free" },
    { id: "hv", label: "HV", tier: "free" },
    { id: "participantwise", label: "Participantwise Positions", tier: "free" },
    { id: "fii-data", label: "FII Data", tier: "free" },
    { id: "indices", label: "Indices", tier: "free" },
  ]},
  { id: "reference", label: "Reference", items: [
    { id: "glossary", label: "Glossary" },
  ]},
];

const CONTENT = {
  overview: {
    kind: "section",
    title: "Platform overview",
    lead: "How charts.vtrender.com is organised, and what to open first.",
    body: [
      { h: "The sidebar" },
      { p: "The left sidebar is organised into three tiers — Elite, Professional, Free Access — plus Subscription, User Settings, and the Manual. The tier a tool belongs to is where it sits in the sidebar. A free subscription opens the Free Access tools. A Professional plan adds Advanced Market Profile, Spectrum, and the options-flow tools. An Elite plan adds Order Flow, Gamma Density, Gamma Exposure, and the replay." },
      { h: "Data" },
      { p: "Charts on the Professional and Elite tiers run on a live NSE and BSE feed. Free Access tools run on a 15-minute delayed feed per exchange licensing. The Options Table, OI Viz, and related tools update at the same cadence." },
      { h: "Two persistent references" },
      { p: "The top-right bar has a Glossary and a User Manual button. Both are open on every page. The Glossary defines every term used inside the charts (IB, IS, COT, NPOC, VAH, VAL, DPOC, and the rest). The User Manual is this walkthrough." },
      { h: "What to open first" },
      { p: "If this is the first session, open the tool that corresponds to the highest tier of the current plan. That tool is the reason the plan exists. Everything else is context around it." },
    ],
  },
  layers: {
    kind: "section",
    title: "The three layers",
    lead: "Structure, Options Flow, and Intent. Every tool on the platform belongs to one of them.",
    body: [
      { h: "Layer 1 — Structure" },
      { p: "Where value formed. What type of day it was. Where the Initial Balance set the range. Where yesterday's DPOC and Value Area sit relative to today. The structure layer is Market Profile. It answers: where." },
      { tools: ["advanced-mp", "mp-bookmarks"] },
      { h: "Layer 2 — Options Flow" },
      { p: "Where options writers are concentrated. Whether they are comfortable or under pressure. What near-the-money activity looks like. What the market expects in terms of total movement. The options-flow layer covers positioning, range expectation, and writer stress. It answers: why." },
      { tools: ["spectrum", "ntm-volx", "ntm-oi", "wcash", "straddles", "options-table", "oi-viz", "total-oi", "iv-smile"] },
      { h: "Layer 3 — Intent" },
      { p: "Who is buying and selling at each price with size, right now. The Order Flow chart reads the exchange tick feed and classifies every trade as initiative or responsive. Gamma Density maps the hedging pressure across every strike. This layer answers: who, and when." },
      { tools: ["orderflow", "gamma-density", "gamma-exposure", "smart-candlesticks", "orderflow-replay"] },
      { h: "Why it matters in that order" },
      { p: "Structure comes first because levels exist before anyone acts on them. Options flow comes second because positioning creates the pressure at those levels. Intent comes last because it is the act itself — the resolution of the level. Reading a session in that order is faster than reading it any other way." },
    ],
  },
  controls: {
    kind: "section",
    title: "Chart controls",
    lead: "Layouts, PPR, timeframe, GV/GVN, Save As, bookmarks.",
    body: [
      { h: "Layout buttons — the right edge" },
      { p: "The right edge of every chart has layout icons. Single pane, vertical split (two panes side by side), horizontal split, 2x2 grid. A common Elite setup is NIFTY Order Flow on the left and BANKNIFTY Order Flow on the right. A Professional setup is NIFTY Advanced Market Profile on the left and Spectrum on the right." },
      { h: "PPR — Price Per Row" },
      { p: "The PPR control at the top adjusts the resolution of the chart. Lower PPR (1 or 2) shows finer detail per row. Higher PPR (5 or 10) shows broader structure across multiple days. Intraday reads use 2. Multi-day comparison uses 5." },
      { h: "Timeframe" },
      { p: "The timeframe buttons at the bottom (1, 3, 5, 15, 30) switch the Order Flow chart between minute resolutions. A 3-minute chart is the default for live sessions. A 15-minute chart compresses the session for structure." },
      { h: "GV and GVN" },
      { p: "GV adds a Global Volume Profile — a horizontal histogram on the right edge showing total volume at each price across the visible range. GVN adds the actual volume numbers next to each row. Together they show which prices attracted the most participation across the full visible window." },
      { h: "Save As and bookmarks" },
      { p: "Save As in the right sidebar stores the current chart state — symbol, timeframe, layout, overlays. Reload it next session with one click. MP Bookmarks (Professional) and Global Bookmarks (Elite) save specific chart configurations for recurring setups: expiry week, pre-budget, post-RBI." },
    ],
  },
  routines: {
    kind: "section",
    title: "Pre-market routines",
    lead: "Three versions — one for each tier. Each takes under ten minutes.",
    body: [
      { h: "Free tier routine" },
      { p: "Six data points from six tools, before 9:15." },
      { ol: [
        "Options Table. Note the highest Call OI and highest Put OI strikes. Expected range.",
        "OI Viz. Did those strikes shift overnight? Migration direction is the new consensus.",
        "Total OI. Rising means new conviction entering. Falling means unwinding.",
        "IV Smile. Is the curve elevated or compressed versus HV? Market pricing a move or not.",
        "FII Data. Net long or short index futures? Changed from yesterday?",
        "Indices. Was yesterday's move broad or narrow across components?",
      ]},
      { h: "Professional tier routine" },
      { p: "Structure plus options flow. Before 9:15." },
      { ol: [
        "Advanced Market Profile — yesterday's session. Day type, DPOC, VAH, VAL.",
        "Spectrum — current range boundaries. Same as yesterday or shifted?",
        "WCash™ — closed positive or negative? Writers comfortable or under pressure entering today.",
        "NTM VolX™ — early options activity elevated or flat? Sets expectations for today's vol.",
        "Options Table — highest OI strikes align with Spectrum boundaries or not?",
        "Straddles — ATM straddle price versus yesterday's close. More or less movement expected?",
      ]},
      { p: "At 9:15, check where the market opens relative to yesterday's Value Area. Open inside is continuation. Open outside is a new narrative." },
      { h: "Elite tier routine" },
      { p: "All three layers, ten minutes." },
      { ol: [
        "Order Flow — yesterday's session on 3-min. DPOC, closing COT, session shape.",
        "Advanced Market Profile — day type. Normal, Trend, Balanced, Double Distribution.",
        "Gamma Density — peak locations. Structural boundaries for today.",
        "Options Table — high-OI strikes. Do they align with gamma peaks?",
        "Spectrum — current range. Market opening inside or outside?",
        "NPOCs within 200 points of current price — anything that might get tested.",
      ]},
    ],
  },
  glossary: {
    kind: "section",
    title: "Glossary",
    lead: "The top-right Glossary button on charts.vtrender.com opens the full list. These are the terms used across the walkthrough.",
    body: [
      { defs: [
        ["DPOC", "Day Point of Control. The single price where the most volume traded in the session. Appears as a yellow horizontal line on Order Flow."],
        ["VPOC", "Volume Point of Control. Same concept applied to a specific bar or period rather than the whole session."],
        ["VWAP", "Volume Weighted Average Price. Institutional desks measure fills against it. Green line on Order Flow."],
        ["VAH / VAL", "Value Area High / Low. The upper and lower bounds of the band containing 70% of session volume."],
        ["IBH / IBL", "Initial Balance High / Low. The range set in the first hour, 9:15 to 10:15 on NSE."],
        ["IB", "Initiative Buyers. Aggressive lifting of the ask. New long positions."],
        ["IS", "Initiative Sellers. Aggressive hitting of the bid. New short positions."],
        ["RB / RS", "Responsive Buyers / Sellers. Covering, not initiating."],
        ["COT", "Commitment of Trade. Net aggressive buys minus aggressive sells within a bar."],
        ["NPOC", "Naked Point of Control. A previous session's DPOC that has not been revisited."],
        ["TPO", "Time Price Opportunity. The letter representation of a 30-minute period on Market Profile."],
        ["Failed auction", "Price extends beyond a value area, finds no acceptance, and returns inside. Rejection stamp."],
        ["Single print", "A price visited by only one letter on Market Profile. Price moved through fast."],
        ["Gamma", "The rate at which an option's Delta changes as the underlying moves. Participants with long gamma gain from movement; participants with short gamma gain from stillness."],
        ["Gamma Exposure", "The balance between participants positioned for movement (long gamma) and participants positioned for a pin (short gamma). The session's regime is set by which side is dominant."],
      ]}
    ],
  },
  "orderflow": {
    kind: "tool", tier: "elite", title: "Order Flow",
    purpose: "Tick-by-tick buying and selling at each price, classified as aggressive (initiative) or covering (responsive). The intent layer.",
    where: "Sidebar → Elite → Orderflow",
    data: "NSE tick feed, live",
    screenshot: { src: "/assets/tools-walkthrough/Orderflow_Chart_with_IbIS_cluster_yagid3.png", alt: "Order Flow chart with Live Panel, annotated IS cluster at 22457.20 and IB marker at 22425.90. Timeframe controls at bottom, layout switchers to the right." },
    fields: [
      ["DPOC", "Day Point of Control. The price where the highest volume traded today. Yellow horizontal line."],
      ["VWAP", "Volume Weighted Average Price. Green line. Institutional fills measured against it."],
      ["VAH / VAL", "Value Area bounds. 70% of session volume. Inside = balanced. Outside = testing."],
      ["IBH / IBL", "Initial Balance bounds. First hour range, 9:15–10:15. Anchors the session."],
      ["COT", "Commitment of Trade. Net aggressive buys minus sells within the bar. Positive = green."],
      ["10 Bar COT", "Rolling sum of last ten bars' COT. Momentum building or fading."],
    ],
    colours: [
      { tag: "IB", name: "Initiative Buyers", desc: "Aggressive lift of the ask. New longs.", c: "green" },
      { tag: "IS", name: "Initiative Sellers", desc: "Aggressive hit of the bid. New shorts.", c: "pink" },
      { tag: "RB", name: "Responsive Buyers", desc: "Short covering. Exiting, not initiating.", c: "gray" },
      { tag: "RS", name: "Responsive Sellers", desc: "Longs taking profit. Closing, not initiating.", c: "gray" },
    ],
    rules: {
      title: "How IB and IS signals work",
      items: [
        { h: "The signal itself", p: "An IB bar is a green marker printed on a bar where aggressive buying crossed the engine's threshold. An IS bar is the same on the sell side in purple. The bar itself is the signal — it does not need confirmation from another indicator." },
        { h: "Timeframe matters", p: "Signals are read on the timeframe of the bar. An IB on a 30-minute bar (IB30™) is the cleanest read for new readers. IB15 compresses the read to 15 minutes; IB5 and IB3 tighten it further but require faster decisions and more screen time. Start on IB30™. Get confident there before moving finer." },
        { h: "The 10-bar window", p: "Every IB or IS signal carries forward for 10 bars of the same timeframe. A IB30™ signal therefore holds for 30 × 10 = 300 minutes — one full session. An IB5 holds for 50 minutes. An IS15 holds for 150 minutes. During that window, the signal is considered live." },
        { h: "Negation", p: "An IB signal is negated in one of two ways. A counter IS signal on the same timeframe prints — the path has flipped. Or the 10-bar window runs out with no follow-through — the signal has expired. Mirror the rule for IS signals." },
        { h: "PLR — path of least resistance", p: "A IB30™ signal is a statement that the path of least resistance of the market has shifted higher. A IS30™ signal is the mirror — PLR has shifted lower. The PLR remains in that direction until a counter signal on the same timeframe prints or the 10-bar window expires." },
        { h: "Entry and stop", p: "When price moves above the high of a green IB bar, longs can be initiated with the stop below the low of the same bar. The inverse for IS bars. The IB30™ bar's own high and low act as the trigger and the invalidation." },
      ]
    },
    observe: [
      "Where did today open relative to yesterday's Value Area? Above VAH, below VAL, or inside.",
      "Where is the largest IB or IS cluster of the session, and did price follow through or reverse from it?",
      "Is the 10 Bar COT building in the direction price is moving, or diverging from it?",
      "Is there a live IB30™ or IS30™ signal on the chart? If yes, the PLR is set. If both have printed within the session, the later one is active until negated.",
    ],
    deskLog: { when: "30-Mar-2026, 14:03", text: "NIFTY 22457.20. IS cluster prints on the 14:00 bar, 589 contracts. IS30™ signal active — PLR lower for the next 300 minutes or until a IB30™ prints against it. 14:03 to 14:12 shows an IB cluster at 22425.90 on the 3-min, 133 contracts — that is an IB3, not a IB30™. DPOC migrates from 22330 to 22425. 10 Bar COT turns from −524 to +61. VWAP at 22490 acts as overhead. Reading: the IS30™ still holds the path lower; the IB3 is a responsive bounce, not a trend flip. Close on or below VWAP confirms absorption; close above and the read starts to shift." },
    pairs: ["advanced-mp", "gamma-density", "spectrum", "smart-candlesticks"],
  },
  "orderflow-replay": {
    kind: "tool", tier: "elite", title: "Order Flow Replay",
    purpose: "Any historical session, played back bar by bar. Pause, step, or run at 2x and 5x speed.",
    where: "Sidebar → Elite → Orderflow Replay",
    data: "Historical NSE tick feed",
    screenshot: { src: "/assets/tools-walkthrough/Orderflow_Replay_twqygv.png", alt: "Orderflow Replay — price-by-price ladder view. Left sidebar shows the Orderflow Replay entry point; PPR control at top; instrument selector and market stats panel on the left inset." },
    fields: [
      ["Date picker", "Any historical trading session."],
      ["Speed", "1x, 2x, 5x, or step by single bar."],
      ["Pause", "Freeze at any bar. Inspect the Live Panel state."],
    ],
    observe: [
      "Replay a session already read post-market. Watch IB and IS clusters form without hindsight. Pause before known inflection points and ask what the chart showed at that moment.",
      "Replay an expiry-day or event-day session. The structure there differs from normal sessions.",
      "Replay the same session on Order Flow and on Smart Candlesticks™. Same tape, two reads.",
    ],
    deskLog: { when: "Exercise", text: "Pick last Thursday's session. Replay at 2x. Pause at every bar where an IS cluster prints. Ask: did price continue down, reverse, or drift? Log the result. After five sessions, the distribution of outcomes is the pattern recognition." },
    pairs: ["orderflow", "advanced-mp", "smart-candlesticks"],
  },
  "smart-candlesticks": {
    kind: "tool", tier: "elite", title: "Smart Candlesticks™",
    purpose: "Traditional candlestick chart with Order Flow and Market Profile data embedded. The bridge for readers coming from a candle background.",
    where: "Sidebar → Elite → Smart Candlesticks™",
    data: "NSE tick feed, live",
    screenshot: { src: "/assets/tools-walkthrough/Smart_Candlesticks_sp0hdw.png", alt: "Smart Candlesticks™ replay chart. IB bars in yellow mark Initial Balance range. IS bars in pink mark initiative/impulsive moves. Example shows price rejection at IB low followed by continuation down, then break above IB high." },
    fields: [
      ["Candle body", "Standard OHLC with IB/IS overlay markers where clusters printed inside the candle."],
      ["Volume profile", "Per-candle volume distribution, visible on expansion."],
      ["Micro Balance™", "Tight volume/range boxes where accumulation occurred inside a candle. Visible as labelled rectangles."],
    ],
    observe: [
      "A large green candle may be IB-driven (new longs, follow-through likely) or RB-driven (short covering, stalls likely). Smart Candlesticks™ shows which.",
      "Micro Balance™ boxes inside a candle mark where price paused and accumulated. A break of the box's high with IB above is continuation. A break lower with IS is rejection.",
      "The same session on candles versus Order Flow highlights what each chart hides. Candles hide intent. Order Flow hides shape.",
    ],
    deskLog: { when: "15-Apr-2026, 11:18", text: "NIFTY23700CE. Yesterday candle: Volume 18847, Range 54.20. Today: gap up, first candle Volume 10339, Range 92.10. Micro Balance™ box on the prior day compressed; today's candle breaks clean with IB markers. Option premium lifts 200 points in two candles. The compression-then-break pattern reads the same whether the underlying is an index or an option." },
    pairs: ["orderflow", "advanced-mp", "orderflow-replay"],
  },
  "gamma-density": {
    kind: "tool", tier: "elite", title: "Gamma Density",
    purpose: "The strike distribution most likely to pull price toward the close. Density is a predictor of where the session settles. Option traders align around the expected close, and the chart shows where that alignment is forming.",
    where: "Sidebar → Elite → Gamma Density",
    data: "Live options chain, recomputed through the session",
    screenshot: { src: "/assets/tools-walkthrough/Gamma_Density_jmzn6y.png", alt: "NIFTY Gamma Density chart. Annotated: convexity zone (green curve), ±1 sigma markers, lower tail at 24200, upper tail at 25050, density curve in orange, spot at 24570.8." },
    fields: [
      ["Density curve", "The orange line. Height at each strike measures the volume of interest in that strike as read by the Orderflow engine — demand against supply, long gamma against short gamma. Taller bars mean stronger pull toward that price."],
      ["Convexity zone", "The wider shaded band. Where the density transitions sharpest. The shape and skew of this zone is the direction the options market is leaning."],
      ["±1 Sigma", "Yellow bounds of the expected move range derived from the current IV regime."],
      ["Upper / Lower tail", "The thinning edges of the convexity zone. A long tail on one side tells you the options market is positioned for price to travel in that direction."],
    ],
    observe: [
      "Density as a close predictor. When the density curve rises and meets the convexity zone, the chance of a pin at that strike is higher. The taller the density bar, the stronger the pull. Option traders align to this expected close.",
      "Convexity shape tells direction. A long tail on the left is the options market telling you price is headed lower — positioning is leaning that way. A long tail on the right is the mirror. Symmetric convexity is no directional lean.",
      "Sharp drop-off is resistance in play. A sudden fall in density on the right side of spot means strikes above are loaded with resistance. The mirror on the left side is support. These drop-offs are where hedging flow from large desks tends to defend.",
      "When Order Flow shows an IS cluster at a price where Gamma Density peaks or drops off sharply, that is not coincidence. The IS is likely hedging flow from large desks carrying options exposure at that strike.",
    ],
    deskLog: { when: "Intraday", text: "NIFTY spot 24570.8 at 15:29. Density curve peaks at 24650 and meets the convexity zone cleanly — tall bar, strong pull. Convexity runs 24540 to 24780 with a longer tail on the right side and a sharp drop-off at 24550. Reading: options market leans higher, close bias toward 24650 as the pin. The sharp drop-off at 24550 is the defended floor. A move to 24550 runs into hedging from large desks carrying exposure there. Close above 24650 breaks the peak and the whole density map rebuilds for tomorrow." },
    pairs: ["orderflow", "gamma-exposure", "spectrum", "options-table"],
  },
  "gamma-exposure": {
    kind: "tool", tier: "elite", title: "Gamma Exposure",
    purpose: "The balance between participants positioned for movement and participants positioned for a pin. Long gamma wants the market to move. Short gamma wants it to stay.",
    where: "Sidebar → Elite → Gamma Exposure",
    data: "Live options chain, recomputed through the session",
    screenshot: { src: "/assets/tools-walkthrough/Gamma_Exposure_xf2qc3.png", alt: "NIFTY Gamma Exposure chart. Buy Exposure in green, Sell Exposure in orange, Neutral Exposure in white. Spot at 24570.8. Dominant buy-side cluster visible above spot; sell-side more active on the lower side." },
    fields: [
      ["Long gamma side", "Long Calls, long Puts, and long-gamma option spreads. These participants gain as the market moves further from their strike. They want range expansion."],
      ["Short gamma side", "Short Calls, short Puts, short strangles, short credit spreads. These participants gain as the market sits still near their strikes. They want a pin."],
      ["Net exposure", "The aggregate across both sides. Positive net is long-gamma dominance. Negative net is short-gamma dominance."],
      ["Zero line", "Where the two sides cancel. Crossing it flips which side carries the session."],
      ["Time series", "How the balance is moving through the session. The direction of change matters more than the level itself."],
    ],
    observe: [
      "Long-gamma dominant: the market is carrying buyers of movement. Expect range expansion, trending sessions, larger intraday swings. Straddle buyers are in control.",
      "Short-gamma dominant: the market is carrying sellers of movement. Expect compression, mean-reversion, rotations around a pin. Straddle sellers are in control.",
      "When gamma flips side intraday, the character of the session changes. A market that opened trending starts to rotate; a market that opened rotating starts to trend. The flip itself is the signal, not the level.",
    ],
    deskLog: { when: "Regime read", text: "Long-gamma dominant on open. Straddle price elevated versus prior close. Expectation: trending session, range extensions. First IB cluster above IBH confirms. By 13:00 the balance flips — short gamma takes over as premium decays into the close. The character changes, price starts to rotate back into balance. The flip at 13:00 was the read, not the open reading." },
    pairs: ["gamma-density", "straddles", "orderflow", "wcash"],
  },
  "global-bookmarks": {
    kind: "tool", tier: "elite", title: "Global Bookmarks",
    purpose: "Saved chart states across all tools. Reload a specific Order Flow / Market Profile / Gamma setup with one click.",
    where: "Sidebar → Elite → Global Bookmarks",
    data: "User-saved configurations",
    screenshot: { src: "/assets/tools-walkthrough/Global_Bookmarks_zd8h7z.png", alt: "Global Bookmarks panel. Saved chart templates listed by Page Layout (symbols and chart types), Last Updated timestamp, Open and Copy URL actions per row, Edit and Delete controls." },
    fields: [
      ["Bookmark", "A saved chart state: symbol, timeframe, layout, overlays."],
      ["Categories", "Organise bookmarks by setup type — expiry, event, day type."],
    ],
    observe: [
      "Bookmark the same view across consecutive expiry weeks. The comparison surfaces what changed in positioning.",
      "Bookmark a clean trend day, a clean balanced day, and a clean double distribution. Use them as reference templates.",
      "When the same setup recurs, open the bookmark first. Compare the current chart to the historical one before acting.",
    ],
    deskLog: { when: "Workflow", text: "Every Thursday evening, bookmark the expiry-day Order Flow and Market Profile. Name it by date and day type. Over a quarter, the library becomes a local pattern library — not from a textbook, from this desk's actual sessions." },
    pairs: ["orderflow-replay", "mp-bookmarks", "advanced-mp"],
  },
  "advanced-mp": {
    kind: "tool", tier: "pro", title: "Advanced Market Profile",
    purpose: "The session as a time-price distribution. Letters stacked at each price for each 30-minute period. Value, shape, and day type at a glance.",
    where: "Sidebar → Professional → Advanced Market Profile",
    data: "Live NSE / BSE feed",
    screenshot: { src: "/assets/tools-walkthrough/Advanaced_Marketprofile_nhiicv.png", alt: "Advanced Market Profile showing multi-day TPO distribution from 03-03 through 31-03-2026. Anchored KRLs mark prior session DPOC, VAH, VAL. Pink horizontal lines at 23613 and 23385 carry forward as the month's value walls." },
    fields: [
      ["TPO letters", "A for 9:15–9:45, B for 9:45–10:15, and so on. Stacked where trading occurred."],
      ["POC", "The single widest row of letters. The session's point of control."],
      ["Value Area", "The highlighted band containing 70% of TPOs. VAH at top, VAL at bottom."],
      ["Single prints", "Rows visited by only one letter. Thin sections that price moved through quickly."],
      ["Anchored KRLs", "Key Reference Levels anchored to prior sessions — previous DPOC, VAH, VAL, carried forward."],
    ],
    observe: [
      "The profile's shape is the day type. Bell-shaped is a Normal day. Elongated thin is a Trend day. Two-humped is a Double Distribution. Compact symmetric is Balanced.",
      "A failed auction is a one- or two-letter tail at the extreme — price went there, no one followed, it came back. Rejection stamp at that level.",
      "An NPOC (naked point of control) is a previous DPOC not yet revisited. Shown as a pink price label on the right edge. Most get tested eventually.",
    ],
    deskLog: { when: "Multi-day read", text: "Mar 17 shows a bell. Mar 18 trends down, thin elongated profile. Mar 19 opens below Mar 18 VAL — new narrative. Value migrates to 23250–23350. Mar 25 returns to 23445 in a compact Balanced shape. Two anchored KRLs (pink lines at 23613 and 23385) mark the month's upper and lower value walls. Price inside those walls is rotation. Outside is a new phase." },
    pairs: ["orderflow", "spectrum", "mp-bookmarks"],
  },
  "spectrum": {
    kind: "tool", tier: "pro", title: "Spectrum",
    purpose: "The price range options writers are defending, overlaid on the spot chart. The structural range derived from positioning.",
    where: "Sidebar → Professional → Spectrum (→ NIFTY / BANKNIFTY / FINNIFTY / MIDCPNIFTY / SENSEX)",
    data: "Live NSE / BSE options positioning",
    screenshot: { src: "/assets/tools-walkthrough/Spectrum_cvtqiq.png", alt: "NIFTY Spot Spectrum chart. Upper and lower boundaries drawn around the spot chart, annotated 'positions adjusting' at the intraday staircase shifts, and 'max positions here' at the strike with the largest concentration." },
    fields: [
      ["Upper boundary", "The top of the writer-defended range. Resistance by positioning."],
      ["Lower boundary", "The bottom of the range. Support by positioning."],
      ["Max positions", "The strike with the largest concentration of writer exposure. Strongest magnet."],
      ["Positions adjusting", "Intraday shifts in the boundaries as writers re-hedge. Visible as staircase moves in the boundary lines."],
    ],
    observe: [
      "Price inside the Spectrum range tends to oscillate between the boundaries. Price breaking out forces hedging that can accelerate the move.",
      "When the boundaries step lower intraday, writers are rolling positions down. Bearish re-positioning.",
      "The max-positions strike is where the gravitational pull is strongest. Expiry days tend to settle near it.",
    ],
    deskLog: { when: "Intraday", text: "NIFTY opens at 22700, Spectrum upper at 22800, lower at 22500. Price rejects 22800 mid-morning — position-adjusting staircase down. By noon, lower boundary steps to 22450. Close at 22380, outside the adjusted lower. Max positions moved from 22600 to 22400 across the day. Tomorrow opens with a new pivot." },
    pairs: ["gamma-density", "options-table", "orderflow", "wcash"],
  },
  "ntm-volx": {
    kind: "tool", tier: "pro", title: "NTM VolX™",
    purpose: "Demand and supply imbalance at the options strikes nearest spot. Call volume against Put volume, side by side with price, with a strength pane underneath.",
    where: "Sidebar → Professional → NTM VolX™",
    data: "Live NSE / BSE options volume",
    screenshot: { src: "/assets/tools-walkthrough/NTM_Volx_gp1mro.png", alt: "NIFTY All Options NTM VolX™ chart on the left (Call VolX in blue, Put VolX in purple) next to NTM OI Change chart on the right. Spot overlay and Net NTM dOI panel visible." },
    fields: [
      ["Call VolX", "Aggregate volume at the Call strikes nearest spot. Blue line. Rising through Call buys or short covering of Call positions."],
      ["Put VolX", "Aggregate volume at the Put strikes nearest spot. Purple line. Rising through Put buys or short covering of Put positions."],
      ["Imbalance read", "Blue above purple is a market where Calls dominate — price bias is higher. Purple above blue is the mirror — price bias is lower."],
      ["VXR pane", "The lower panel. Green or red bars measuring the strength of the trend in the imbalance. A reading above 4 is a strongly one-sided market with clean risk-reward for trades in that direction."],
      ["Spot overlay", "The underlying price, aligned with the volume panels for comparison."],
    ],
    observe: [
      "The 45° condition. One line rising at 45° while the other stays flat. That pattern marks a market with strong imbalance and excellent risk-reward — one side is carrying the tape, the other has stepped away.",
      "Both lines rising together. Demand is expanding on both sides. The market expects a bigger move but has not chosen a direction. Straddle-buyer behaviour.",
      "VXR above 4. Trend strength is clean. Read the imbalance direction (blue-dominant or purple-dominant) and bias trades with it. VXR below 2 is a chop tape — the imbalance reads are less reliable.",
    ],
    deskLog: { when: "Intraday — 45° read", text: "NIFTY opens 25094. Put VolX flat at 224 through the first hour. Call VolX drifts from 180 up through 216 — the classic 45°. Blue rising, purple flat. Imbalance reads bullish: Call buyers active, Puts uncommitted. VXR turns green at 11:00, crosses 4 by 11:30. Spot lifts from 25094 to 25140 over the next hour. The imbalance was visible 45 minutes before price confirmed the read." },
    pairs: ["ntm-oi", "wcash", "straddles", "options-table"],
  },
  "ntm-oi": {
    kind: "tool", tier: "pro", title: "NTM OI",
    purpose: "Open interest at near-the-money strikes. Accumulated positions rather than today's activity.",
    where: "Sidebar → Professional → NTM OI",
    data: "Live NSE / BSE OI",
    screenshot: { src: "/assets/tools-walkthrough/NTM_OI_vzptgk.png", alt: "NIFTY All Options NTM OI Change chart. CE Wall and PE Wall strikes, Gamma Ref inflection strike, mViz High/Low visible range, Net NTM dOI session change panel." },
    fields: [
      ["CE Wall", "The Call strike with the largest OI near spot."],
      ["PE Wall", "The Put strike with the largest OI near spot."],
      ["Gamma Ref", "The inflection strike derived from the OI distribution."],
      ["mViz High / Low", "Near-term visible range from OI concentration."],
      ["Net NTM dOI", "Net change in OI at NTM strikes during the session."],
    ],
    observe: [
      "Rising NTM OI with rising NTM VolX™ is new conviction — fresh positions being built at current price.",
      "Rising NTM VolX™ with flat NTM OI is churn — existing positions trading without new commitment.",
      "The CE and PE walls define the consensus trading range for the session.",
    ],
    deskLog: { when: "Paired read with NTM VolX™", text: "CE Wall 25200, PE Wall 24900, Gamma Ref 25100. Spot 25095, just below the gamma reference. NTM OI builds on the Put side through the morning — writers adding at 24900. Net NTM dOI positive. Read: defending the lower edge. Break below 24900 would flip the writers into defence mode, typically drags IV up." },
    pairs: ["ntm-volx", "options-table", "spectrum", "oi-viz"],
  },
  "wcash": {
    kind: "tool", tier: "pro", title: "WCash™",
    purpose: "Writer Cash. The aggregate mark-to-market of options writers across the session. Positive means writers are comfortable. Negative means pressure.",
    where: "Sidebar → Professional → WCash™",
    data: "Live options pricing and positioning",
    screenshot: { src: "/assets/tools-walkthrough/Wcash_-_Writers_Cash_nszfx2.png", alt: "WCash™ chart — Writers Cash line through the session showing aggregate options writer P&L. Positive regions mark writer comfort; falling sections mark pressure and forced hedging." },
    fields: [
      ["WCash™ line", "Aggregate writer P&L through the session. Rises when price sits in the written range; falls when price threatens strikes."],
      ["Close reading", "End-of-session WCash™ sets tomorrow's starting pressure."],
      ["Intraday deterioration", "A sharp fall in WCash™ means writers are forced to hedge. Hedging flow moves price."],
    ],
    observe: [
      "WCash™ strongly positive and rising: writers comfortable, range-bound pressure.",
      "WCash™ deteriorating: writers under pressure. Hedging can accelerate a directional move.",
      "An IS cluster (on Order Flow) at a price where WCash™ is deteriorating carries more weight than the same cluster with writers comfortable.",
    ],
    deskLog: { when: "End-of-session", text: "WCash™ closed negative −₹12cr on expiry Wednesday. Writers went in with ₹4cr positive. Pressure built through the afternoon as price pushed toward the 23500 Call wall. Thursday open: straddles elevated, writers start defensive. Short-gamma side dominant on Gamma Exposure — confirms writers are the ones under stress." },
    pairs: ["straddles", "spectrum", "gamma-exposure", "orderflow"],
  },
  "straddles": {
    kind: "tool", tier: "pro", title: "Straddles",
    purpose: "Live at-the-money straddle price through the session. The market's running estimate of expected movement.",
    where: "Sidebar → Professional → Straddles",
    data: "Live ATM option pricing",
    screenshot: { src: "/assets/tools-walkthrough/Straddles_rowx3n.png", alt: "Straddles chart showing live ATM straddle price through the session. The intraday path is the market's running estimate of expected movement — rising on event anticipation, compressing on quiet tape." },
    fields: [
      ["ATM straddle price", "Sum of ATM Call and ATM Put premium. The implied cost of either direction."],
      ["Intraday path", "How the straddle repriced through the session."],
      ["Session open vs prior close", "Where the day starts relative to yesterday's close. Expectation of more or less movement."],
    ],
    observe: [
      "Rising straddle price means the market expects larger moves. Confirms trending conditions when paired with long-gamma dominance on Gamma Exposure.",
      "Falling straddle price means compression — an expected smaller range. Often precedes range expansion.",
      "On event days (RBI, budget, expiry), the straddle path through the event is the market's live reaction.",
    ],
    deskLog: { when: "RBI day", text: "NIFTY ATM straddle ₹180 at open, drifting to ₹165 by 11:30 as nothing happened. 12:00 RBI announcement, straddle spikes to ₹220 then drains back to ₹170 over the following hour. The spike-and-drain is the classic event pattern — implied move priced in, then released." },
    pairs: ["wcash", "gamma-exposure", "iv-smile", "ntm-volx"],
  },
  "mp-bookmarks": {
    kind: "tool", tier: "pro", title: "MP Bookmarks",
    purpose: "Saved Market Profile chart states. Comparative reference for recurring setups.",
    where: "Sidebar → Professional → MP Bookmarks",
    data: "User-saved configurations",
    screenshot: { src: "/assets/tools-walkthrough/MP_Bookmarks_dgyrqb.png", alt: "MP Bookmarks panel. Saved Market Profile views listed with symbol, date range, TPO size and overlays. Open, edit, and share controls per row." },
    fields: [
      ["Bookmark", "A saved MP view: symbol, date range, TPO size, overlays."],
      ["Tags", "Optional labels — expiry, post-event, gap-open, range-day."],
    ],
    observe: [
      "Bookmark the session before every major event. After the event, the before/after pair shows what positioning did.",
      "Bookmark clear examples of each day type. They become the reference library for shape recognition.",
      "Revisit bookmarks before similar setups — pre-budget, pre-RBI, expiry week. Compare the current building profile to the historical one.",
    ],
    deskLog: { when: "Workflow", text: "Before every expiry Thursday, open the previous four expiry-day MP bookmarks. Note the shape each ended in — trend, balanced, double distribution. The sequence is the base rate. Today's live profile is then read against that base rate." },
    pairs: ["advanced-mp", "global-bookmarks"],
  },
  "options-table": {
    kind: "tool", tier: "free", title: "Options Table",
    purpose: "Live options chain. Premium, volume, OI, change in OI, and IV at every strike. The primary positioning view.",
    where: "Sidebar → Free Access → Options Table",
    data: "15-minute delayed NSE / BSE options chain",
    screenshot: { src: "/assets/tools-walkthrough/Options_Table_zdrltx.png", alt: "Options Table — live chain view with strike, LTP, Volume, OI, dOI and IV columns across Call and Put sides." },
    fields: [
      ["LTP", "Last traded premium at each strike."],
      ["Volume", "Contracts traded today at the strike."],
      ["OI", "Open interest — outstanding contracts at the strike."],
      ["dOI", "Change in OI during the session. New positions or closed."],
      ["IV", "Implied volatility at the strike."],
    ],
    observe: [
      "The strikes with the largest OI on Call and Put sides often define the expected range for the expiry.",
      "Rising Call OI with rising premium is new Call writers entering — they expect price to stay below that strike.",
      "Rising Put OI with rising premium is new Put writers — they expect price to stay above that strike.",
      "Falling OI is position closure. Conviction reducing at that strike.",
    ],
    deskLog: { when: "Pre-market", text: "NIFTY 07-Apr expiry. Highest Call OI at 24000, highest Put OI at 23000. Consensus range. dOI shows fresh adds at both — both sides adding, symmetric positioning. Expect rotation between 23000 and 24000 unless one side starts unwinding aggressively." },
    pairs: ["oi-viz", "total-oi", "spectrum", "ntm-oi"],
  },
  "payoff-chart": {
    kind: "tool", tier: "free", title: "Payoff Chart",
    purpose: "P&L at every price at expiry for a chosen combination of strikes, quantities, and directions. Plan before committing capital.",
    where: "Sidebar → Free Access → Payoff Chart",
    data: "Current option prices",
    screenshot: { src: "/assets/tools-walkthrough/Payoff_charts_himlqs.png", alt: "Payoff Chart showing P&L at expiry across every price for a multi-leg position. Breakevens, max profit and max loss regions visible." },
    fields: [
      ["Leg builder", "Add each position: strike, direction, quantity."],
      ["Payoff curve", "P&L at expiry across every price."],
      ["Breakevens", "Prices where P&L crosses zero."],
      ["Max profit / loss", "Bounded region of the payoff."],
    ],
    observe: [
      "Build the position before entering. See the breakeven and the edges of the curve.",
      "A straddle has a flat middle (loss zone) and upward-sloping edges (profit zones). A strangle widens the flat middle.",
      "The shape of the payoff is the strategy. Theta decay and IV moves shift the curve but not its shape.",
    ],
    deskLog: { when: "Planning", text: "Considering a short ATM straddle for expiry day. Build it. Breakevens at 23400 and 23600. Max profit ₹8400 if settles at 23500, max loss unbounded outside breakevens. The payoff confirms: only trade this if the Spectrum max-positions strike aligns with 23500." },
    pairs: ["option-greeks", "iv-smile", "straddles"],
  },
  "option-greeks": {
    kind: "tool", tier: "free", title: "Option Greeks",
    purpose: "Delta, Gamma, Theta, Vega for a chosen position or chain. The sensitivity map.",
    where: "Sidebar → Free Access → Option Greeks",
    data: "Current option prices",
    screenshot: { src: "/assets/tools-walkthrough/Option_Greeks_oeclwf.png", alt: "Option Greeks view with Delta, Gamma, Theta and Vega across strikes." },
    fields: [
      ["Delta", "Change in premium per point of underlying. Direction."],
      ["Gamma", "Change in Delta per point. Acceleration."],
      ["Theta", "Daily time decay. Collection for writers, cost for buyers."],
      ["Vega", "Change in premium per 1% IV change. Volatility exposure."],
    ],
    observe: [
      "ATM options have highest Gamma and Vega. OTM options have higher Theta relative to premium paid.",
      "Selling ATM options maximises theta collection but carries highest gamma risk.",
      "Delta-neutral does not mean risk-neutral. Gamma and Vega still move the P&L.",
    ],
    deskLog: { when: "Position review", text: "Short 23500 Call, held overnight. Delta −0.42, Gamma 0.002, Theta ₹14, Vega −₹3.80. Reading: decay covers a ₹14 overnight cost. A 1% IV pop costs ₹3.80. A 50-point move up adds ~21 points to P&L loss and another 10 from Gamma. Size based on the Gamma leg, not the Delta." },
    pairs: ["payoff-chart", "iv-smile"],
  },
  "iv-smile": {
    kind: "tool", tier: "free", title: "IV Smile",
    purpose: "Implied volatility plotted across all strikes for an expiry. The curve shape is the market's risk pricing.",
    where: "Sidebar → Free Access → IV Smile",
    data: "15-minute delayed IV",
    screenshot: { src: "/assets/tools-walkthrough/IV_smile_c9dpvx.png", alt: "IV Smile curve plotted across all strikes for a single expiry. Put-wing and Call-wing visible; ATM IV anchors the centre." },
    fields: [
      ["IV curve", "Implied volatility at each strike."],
      ["ATM IV", "Implied vol at the at-the-money strike. The headline number."],
      ["Skew", "Difference between Put-side and Call-side IV at equivalent distance."],
    ],
    observe: [
      "A steeper Put wing means the market is pricing more downside risk.",
      "A steeper Call wing is rarer on index options — usually event-driven upside tail pricing.",
      "Flattening of the smile is compression — the market sees less tail risk on either side.",
    ],
    deskLog: { when: "Event week", text: "Pre-budget smile: Put wing steepens 4 vol points above normal. ATM moves from 12% to 16%. The market paid up for downside tails. Post-budget smile flattens by Monday — event uncertainty released." },
    pairs: ["hv", "straddles", "option-greeks"],
  },
  "oi-viz": {
    kind: "tool", tier: "free", title: "OI Viz",
    purpose: "Open interest distribution across strikes, visualised as bars. Day-over-day migration of positioning.",
    where: "Sidebar → Free Access → OI Viz",
    data: "15-minute delayed OI",
    screenshot: { src: "/assets/tools-walkthrough/OI_viz_rophyh.png", alt: "OI Viz — Call OI and Put OI bar distribution across strikes, with historical overlay for day-over-day migration." },
    fields: [
      ["Call OI bars", "One bar per Call strike. Height is contracts outstanding."],
      ["Put OI bars", "Mirror on the Put side."],
      ["Historical compare", "Overlay prior day(s) to see where positioning shifted."],
    ],
    observe: [
      "Strike migration: the highest OI moving from one strike to another is the consensus range moving.",
      "Concentrated buildup at a single strike across days makes that strike a structural wall.",
      "A 30–40% drop in OI at a major strike in one session weakens the wall.",
    ],
    deskLog: { when: "Daily compare", text: "Monday: Call wall at 24000, Put wall at 23500. Tuesday: Call wall shifts to 23800, Put wall unchanged. Range repricing lower. Wednesday: Call wall 23800 holds, Put wall moves to 23400. Both walls stepped down 100 points over three days. Trend positioning." },
    pairs: ["options-table", "total-oi", "spectrum"],
  },
  "total-oi": {
    kind: "tool", tier: "free", title: "Total OI",
    purpose: "Aggregate open interest across all strikes. The single number that says whether new money is entering or leaving.",
    where: "Sidebar → Free Access → Total OI",
    data: "15-minute delayed OI aggregates",
    screenshot: { src: "/assets/tools-walkthrough/Total_OI_aj1pdl.png", alt: "Total OI chart — aggregate open interest across all strikes, session change and multi-day trend." },
    fields: [
      ["Total OI line", "Aggregate outstanding contracts."],
      ["Day change", "Net add or reduction across the session."],
      ["Multi-day trend", "Whether conviction is building or draining over the week."],
    ],
    observe: [
      "Rising Total OI with rising price is longs building. Trending support.",
      "Rising Total OI with falling price is shorts building. Trending resistance.",
      "Falling Total OI with any price direction is unwinding — conviction leaving.",
    ],
    deskLog: { when: "Weekly read", text: "Monday Total OI +4.2L. Tuesday +2.8L. Wednesday −1.1L. Thursday −3.4L (expiry). Friday +5.8L into new expiry. The Monday-Tuesday build created the range. The Wednesday-Thursday drain set up a settle. The Friday re-add is fresh positioning for next week's narrative." },
    pairs: ["oi-viz", "options-table"],
  },
  "total-volumes": {
    kind: "tool", tier: "free", title: "Total Volumes",
    purpose: "Aggregate volume across the index components and options. Participation confirmation.",
    where: "Sidebar → Free Access → Total Volumes",
    data: "15-minute delayed aggregate volumes",
    screenshot: { src: "/assets/tools-walkthrough/Total_Volumes_wo73vs.png", alt: "Total Volumes chart — aggregate volume across index components and options, with 5-day and 14-day average comparisons." },
    fields: [
      ["Index volume", "Cash market volume across index constituents."],
      ["Options volume", "Across the full chain."],
      ["Session comparison", "Today versus 5-day average versus 14-day average."],
    ],
    observe: [
      "A 200-point move on high volume is participation. Same move on low volume is thin conditions.",
      "Volume concentrating in the first and last hour is normal. Midday spikes often mark events.",
      "Low volume at an NPOC break suggests the break may fail. High volume confirms follow-through.",
    ],
    deskLog: { when: "Move confirmation", text: "NIFTY breaks previous day's VAH at 11:15. Total Volumes chart shows today running 120% of 5-day average at that time. Break is on participation. Compare: last Tuesday's break ran 72% — stalled and reversed. Volume is the tiebreaker when structure says break." },
    pairs: ["indices", "orderflow", "advanced-mp"],
  },
  "hv": {
    kind: "tool", tier: "free", title: "HV",
    purpose: "Historical Volatility. Actual past movement measured across multiple lookback periods.",
    where: "Sidebar → Free Access → HV",
    data: "Price history, recomputed daily",
    screenshot: { src: "/assets/tools-walkthrough/Historical_Volaity_HV_p3pcue.png", alt: "Historical Volatility chart with 10-day, 20-day and 30-day realised vol lookbacks overlaid across time." },
    fields: [
      ["10-day HV", "Shortest lookback. Reactive to recent regime."],
      ["20-day HV", "Standard month-equivalent."],
      ["30-day HV", "Smoother, trend of realised vol."],
    ],
    observe: [
      "Converging HVs across lookbacks mean a steady regime. Diverging means regime change in progress.",
      "IV significantly above HV: options expensive versus realised. Writers paid well.",
      "IV significantly below HV: options cheap versus realised. Buyers have edge.",
    ],
    deskLog: { when: "Vol regime", text: "10-day HV 8%, 20-day 11%, 30-day 14%. Divergence — realised vol compressing fast. ATM IV at 12%. IV below 30-day HV but above 10-day. Market pricing more vol than last 10 sessions delivered but less than the last month. Neutral regime, slight premium for writers if the compression continues." },
    pairs: ["iv-smile", "straddles"],
  },
  "participantwise": {
    kind: "tool", tier: "free", title: "Participantwise Positions",
    purpose: "FII, DII, Proprietary, and Client net positions across product categories. The category-level divergences are the signal.",
    where: "Sidebar → Free Access → Participantwise Positions",
    data: "Daily NSE participant data",
    screenshot: { src: "/assets/tools-walkthrough/Participant_changes_geblm5.png", alt: "Participantwise Positions — FII, DII, Proprietary and Client net positions across index futures, index options, stock futures and stock options." },
    fields: [
      ["FII", "Foreign institutional investors. Net position per product."],
      ["DII", "Domestic institutions."],
      ["Pro", "Proprietary desks."],
      ["Client", "Retail and non-institutional."],
    ],
    observe: [
      "FII net long futures while Client net short is the classic divergence. Institutional view differs from retail.",
      "Pro and FII aligned while DII opposite is a contested positioning. Resolution usually comes within days.",
      "Category shifts are slower than price. A multi-day consistent shift tends to precede a regime move.",
    ],
    deskLog: { when: "Weekly read", text: "FII net long index futures +42K contracts, up from +28K Monday. Client net short index futures −38K. Classic divergence. DII slightly net long. Read: institutional money adding long exposure while retail continues to fade the move. Historically the FII side carries." },
    pairs: ["fii-data", "oi-viz"],
  },
  "fii-data": {
    kind: "tool", tier: "free", title: "FII Data",
    purpose: "FII daily activity by product — index futures, index options, stock futures, stock options. Direction and size.",
    where: "Sidebar → Free Access → FII Data",
    data: "Daily NSE FII reports",
    screenshot: { src: "/assets/tools-walkthrough/FII_data_dhkx7y.png", alt: "FII Data — daily FII activity across index futures, index options, stock futures and stock options. Direction and size per product." },
    fields: [
      ["Index futures", "Net long or short in NIFTY / BANKNIFTY futures."],
      ["Index options", "Net Call / Put positions."],
      ["Stock futures / options", "Single-stock derivative activity."],
    ],
    observe: [
      "FII net long futures plus net short options (selling Calls) is range-bound positioning.",
      "FII net long futures plus net long Calls is upside positioning.",
      "A sudden flip in FII futures net (say +30K to −10K) over 1–2 sessions is a repositioning signal.",
    ],
    deskLog: { when: "Pre-market", text: "FII +52K index futures, net sellers of Calls 48K, net buyers of Puts 14K. Reading: long the market but hedged with Put buying. Comfortable long bias with downside protection. Not a pure bullish stance — a hedged one. Expect range behaviour until Put hedges get lifted." },
    pairs: ["participantwise", "options-table"],
  },
  "indices": {
    kind: "tool", tier: "free", title: "Indices",
    purpose: "Decomposition of NIFTY / BANKNIFTY into constituent contributions. Which stocks are moving the index.",
    where: "Sidebar → Free Access → Indices",
    data: "15-minute delayed constituent prices",
    screenshot: { src: "/assets/tools-walkthrough/Indices_qxvnqd.png", alt: "Indices decomposition — constituent contributions to NIFTY / BANKNIFTY moves, breadth (up vs down counts) and concentration (share of move from top contributors)." },
    fields: [
      ["Contribution", "Each constituent's point contribution to the index move today."],
      ["Breadth", "How many constituents up vs down."],
      ["Concentration", "Share of the move coming from top 3, top 5 contributors."],
    ],
    observe: [
      "A 200-point NIFTY move with 40 constituents up is broad participation. Sustainable.",
      "A 200-point move with 3 stocks driving 80% is narrow. Fragile. A reversal in those 3 reverses the index.",
      "Breadth divergences (index up on narrowing breadth) often precede pullbacks.",
    ],
    deskLog: { when: "Move quality", text: "NIFTY +180 points. Indices decomposition: Reliance, HDFC Bank, Infosys contributed +140. 37 of 50 constituents up. Top 3 accounting for 78% of the move is narrow. Watch those three intraday. A reversal in one drops the index 30 points even if breadth is positive." },
    pairs: ["total-volumes", "orderflow"],
  },
};

function TierBadge({ tier }) {
  if (!tier) return null;
  const t = TIERS[tier];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide uppercase"
      style={{
        background: `${t.accent}1A`,
        color: t.accent,
        border: `1px solid ${t.accent}6B`,
        padding: "0.2rem 0.46rem",
        lineHeight: 1.1,
        transform: "translateY(1px)",
      }}
    >
      <span className="inline-block w-1 h-1 rounded-full" style={{ background: t.dot, boxShadow: `0 0 0 1px ${t.dot}55` }} />
      {t.label}
    </span>
  );
}

function ToolPill({ id, onNav }) {
  const tool = CONTENT[id];
  if (!tool) return null;
  return (
    <button
      onClick={() => onNav(id)}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] transition-colors hover:brightness-125 walkthrough-pill"
      style={{ background: "rgba(63, 105, 248, 0.12)", color: "#dce7ff", border: "1px solid rgba(63, 105, 248, 0.35)" }}
    >
      {tool.title}
      <ChevronRight size={12} />
    </button>
  );
}

function sectionIcon(sectionId) {
  if (sectionId === "start") return <Rocket size={12} />;
  if (sectionId === "elite") return <Crown size={12} />;
  if (sectionId === "pro") return <Briefcase size={12} />;
  if (sectionId === "free") return <BadgeDollarSign size={12} />;
  return <Library size={12} />;
}

function Screenshot({ src, alt }) {
  const isPlaceholder = !src || src.startsWith("PLACEHOLDER_");
  if (isPlaceholder) {
    return (
      <div className="rounded-md overflow-hidden my-4" style={{ background: "#0a1628", border: "0.5px solid rgba(255,255,255,0.06)" }}>
        <div className="flex items-center justify-center text-center px-6" style={{ minHeight: 240, color: "#5b7ba8", fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
          <div>
            <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "#3a567d" }}>Screenshot placeholder</div>
            <div className="text-[13px]" style={{ maxWidth: 520 }}>{alt}</div>
            <div className="text-[10px] mt-3" style={{ color: "#2a4466" }}>[{src}]</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <figure className="rounded-md overflow-hidden my-4" style={{ background: "#0a1628", border: "0.5px solid rgba(255,255,255,0.06)" }}>
      <img src={src} alt={alt} style={{ width: "100%", height: "auto", display: "block" }} />
      {alt && (
        <figcaption className="px-3 py-2 text-[11px]" style={{ color: "#6b6960", fontStyle: "italic", borderTop: "0.5px solid rgba(255,255,255,0.04)" }}>
          {alt}
        </figcaption>
      )}
    </figure>
  );
}

function FieldList({ fields }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 my-4">
      {fields.map(([name, desc]) => (
        <div key={name} className="rounded-md px-3.5 py-3" style={{ background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(255,255,255,0.06)", padding: "0.78rem 0.92rem" }}>
          <div className="text-[13px] font-medium mb-1" style={{ color: "#f0ede5", fontFamily: "'IBM Plex Mono', ui-monospace, monospace", marginBottom: "0.38rem" }}>{name}</div>
          <div className="text-[13px] leading-[1.55]" style={{ color: "#a8a69d", lineHeight: 1.68 }}>{desc}</div>
        </div>
      ))}
    </div>
  );
}

function Colours({ colours }) {
  const palette = {
    green: { bg: "rgba(99, 153, 34, 0.16)", border: "#7fb63b", text: "#d2ebb0" },
    pink: { bg: "rgba(153, 53, 86, 0.22)", border: "#b04a72", text: "#f2b6cb" },
    gray: { bg: "rgba(136, 135, 128, 0.16)", border: "#9d9c93", text: "#d2d0c7" },
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 my-4">
      {colours.map((c) => {
        const p = palette[c.c];
        return (
          <div
            key={c.tag}
            className="px-3.5 py-3"
            style={{
              background: p.bg,
              borderLeft: `3px solid ${p.border}`,
              border: `1px solid ${p.border}66`,
              borderRadius: 8,
              padding: "0.78rem 0.92rem",
            }}
          >
            <div className="text-[13px] font-medium" style={{ color: p.text, fontFamily: "'IBM Plex Mono', ui-monospace, monospace", marginBottom: "0.35rem" }}>
              {c.tag} — {c.name}
            </div>
            <div className="text-[12px] mt-1" style={{ color: "#e0e6f3", lineHeight: 1.62 }}>{c.desc}</div>
          </div>
        );
      })}
    </div>
  );
}

function DeskLog({ entry }) {
  return (
    <div
      className="my-4 px-4 py-3.5"
      style={{
        background: "rgba(239, 159, 39, 0.08)",
        borderLeft: "3px solid #d58a1f",
        border: "1px solid rgba(213, 138, 31, 0.35)",
        borderRadius: 8,
        padding: "1rem 1.08rem",
      }}
    >
      <div
        className="text-[11px] uppercase tracking-widest mb-1.5"
        style={{
          color: "#d59a3b",
          fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
          marginBottom: "0.52rem",
        }}
      >
        Desk log — {entry.when}
      </div>
      <div
        className="text-[14px] leading-[1.7]"
        style={{ color: "#e7eaf1", lineHeight: 1.75, paddingBottom: "0.04rem" }}
      >
        {entry.text}
      </div>
    </div>
  );
}

function ToolPage({ tool, onNav }) {
  return (
    <article>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h1 className="text-[28px] leading-tight" style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontWeight: 500, color: "#f0ede5" }}>
          {tool.title}
        </h1>
        <TierBadge tier={tool.tier} />
      </div>

      <p className="text-[16px] leading-[1.6] mb-5" style={{ color: "#c8c6be" }}>{tool.purpose}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-5">
        <div className="rounded-md px-3.5 py-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(122, 156, 255, 0.24)", borderRadius: 8, padding: "0.78rem 0.92rem" }}>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#91a7d7", marginBottom: "0.4rem" }}>Where</div>
          <div className="text-[13px]" style={{ color: "#e2ebff", lineHeight: 1.66 }}>{tool.where}</div>
        </div>
        <div className="rounded-md px-3.5 py-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(122, 156, 255, 0.24)", borderRadius: 8, padding: "0.78rem 0.92rem" }}>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#91a7d7", marginBottom: "0.4rem" }}>Data</div>
          <div className="text-[13px]" style={{ color: "#e2ebff", lineHeight: 1.66 }}>{tool.data}</div>
        </div>
      </div>

      <Screenshot src={tool.screenshot.src} alt={tool.screenshot.alt} />

      {tool.fields && (<>
        <h2 className="text-[17px] mt-7 mb-1" style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontWeight: 500, color: "#f0ede5" }}>Key fields</h2>
        <FieldList fields={tool.fields} />
      </>)}

      {tool.colours && (<>
        <h2 className="text-[17px] mt-7 mb-1" style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontWeight: 500, color: "#f0ede5" }}>The four colours</h2>
        <Colours colours={tool.colours} />
      </>)}

      {tool.rules && (<>
        <h2 className="text-[17px] mt-7 mb-3" style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontWeight: 500, color: "#f0ede5" }}>{tool.rules.title}</h2>
        <div className="space-y-2.5 mb-2" style={{ marginTop: "0.3rem" }}>
          {tool.rules.items.map((r, i) => (
            <div
              key={i}
              className="rounded-md px-4 py-3"
              style={{
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(122, 156, 255, 0.22)",
                borderRadius: 8,
                padding: "0.82rem 0.96rem",
              }}
            >
              <div className="text-[13px] font-medium mb-1" style={{ color: "#f0ede5", marginBottom: "0.38rem" }}>{r.h}</div>
              <div className="text-[13.5px] leading-[1.65]" style={{ color: "#d4dced", lineHeight: 1.7 }}>{r.p}</div>
            </div>
          ))}
        </div>
      </>)}

      {tool.observe && (<>
        <h2 className="text-[17px] mt-7 mb-2" style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontWeight: 500, color: "#f0ede5" }}>Observations to make on every open</h2>
        <ol className="ml-5 space-y-1.5">
          {tool.observe.map((o, i) => (
            <li key={i} className="text-[14.5px] leading-[1.7] pl-1" style={{ color: "#d4d4d0", listStyleType: "decimal" }}>{o}</li>
          ))}
        </ol>
      </>)}

      {tool.deskLog && (<>
        <h2 className="text-[17px] mt-7 mb-1" style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontWeight: 500, color: "#f0ede5" }}>Desk-log example</h2>
        <DeskLog entry={tool.deskLog} />
      </>)}

      {tool.pairs && tool.pairs.length > 0 && (<>
        <h2 className="text-[17px] mt-7 mb-3" style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontWeight: 500, color: "#f0ede5" }}>Pairs with</h2>
        <div className="flex flex-wrap gap-2 mb-6">
          {tool.pairs.map((id) => <ToolPill key={id} id={id} onNav={onNav} />)}
        </div>
      </>)}
    </article>
  );
}

function SectionPage({ section, onNav }) {
  return (
    <article>
      <h1 className="text-[28px] leading-tight mb-2" style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontWeight: 500, color: "#f0ede5" }}>
        {section.title}
      </h1>
      <p className="text-[16px] leading-[1.6] mb-6" style={{ color: "#c8c6be" }}>{section.lead}</p>

      {section.body.map((block, i) => {
        if (block.h) return <h2 key={i} className="text-[17px] mt-6 mb-2" style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontWeight: 500, color: "#f0ede5" }}>{block.h}</h2>;
        if (block.p) return <p key={i} className="text-[15px] leading-[1.75] mb-3" style={{ color: "#c8c6be" }}>{block.p}</p>;
        if (block.ol) return (
          <ol key={i} className="ml-5 space-y-1.5 mb-3">
            {block.ol.map((item, j) => (
              <li key={j} className="text-[14.5px] leading-[1.7] pl-1" style={{ color: "#d4d4d0", listStyleType: "decimal" }}>{item}</li>
            ))}
          </ol>
        );
        if (block.tools) return (
          <div key={i} className="flex flex-wrap gap-2 mb-4">
            {block.tools.map((id) => <ToolPill key={id} id={id} onNav={onNav} />)}
          </div>
        );
        if (block.defs) return (
          <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2.5 my-4">
            {block.defs.map(([term, def]) => (
              <div key={term} className="rounded-md px-3.5 py-3" style={{ background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(255,255,255,0.06)", padding: "0.78rem 0.92rem" }}>
                <div className="text-[13px] font-medium mb-1" style={{ color: "#f0ede5", fontFamily: "'IBM Plex Mono', ui-monospace, monospace", marginBottom: "0.38rem" }}>{term}</div>
                <div className="text-[13px] leading-[1.55]" style={{ color: "#a8a69d", lineHeight: 1.68 }}>{def}</div>
              </div>
            ))}
          </div>
        );
        return null;
      })}
    </article>
  );
}

export default function VtrenderWalkthrough({
    onBack,
    initialActiveId = "overview",
}) {
  const [activeId, setActiveId] = useState(initialActiveId);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const mainRef = useRef(null);
  const isDesktop = useMediaQuery("(min-width:768px)");
  // const learningPathPdf = "/assets/Vtrender%20Learning%20Pathway%20for%20charts%20with%20links.pdf";
  const learningPathPdf = "/assets/Vtrender_9_Pillar_Learning_Pathway_Download_v2.pdf";

  useEffect(() => {
    if (!document.getElementById("plex-fonts")) {
      const link = document.createElement("link");
      link.id = "plex-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500&family=IBM+Plex+Serif:wght@400;500&family=IBM+Plex+Mono:wght@400;500&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [activeId]);

  useEffect(() => {
    setIsSidebarOpen(isDesktop);
  }, [isDesktop]);

  const current = CONTENT[activeId];
  const onNav = (id) => {
    setActiveId(id);
    if (!isDesktop) setIsSidebarOpen(false);
  };

  return (
    <div
      className="walkthrough-page"
      style={{
        width: "100%",
        height: "100%",
        background: "linear-gradient(180deg, #0c1630 0%, #0b1220 100%)",
        color: "#d4d4d0",
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <header
        className="flex items-center justify-between px-5 py-3.5 walkthrough-header"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(11, 18, 32, 0.78)",
          backdropFilter: "blur(6px)",
          position: "sticky",
          top: 0,
          zIndex: 12,
          flexShrink: 0,
          padding: "0.95rem 1.4rem",
        }}
      >
        <div className="flex items-center gap-3 walkthrough-header-left">
          <button
            className=""
            type="button"
            aria-label={isSidebarOpen ? "Hide menu" : "Show menu"}
            onClick={() => setIsSidebarOpen((prev) => !prev)}
            style={{
              color: "#dbe6ff",
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid rgba(122, 156, 255, 0.45)",
              background: "rgba(63, 105, 248, 0.16)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
            }}
          >
            {isSidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div className="flex items-center gap-2.5 walkthrough-header-brand">
            <BookOpen size={16} style={{ color: "#EF9F27" }} />
            <div className="walkthrough-header-title-wrap">
              <div className="text-[14px] font-medium walkthrough-header-title" style={{ color: "#f0ede5", fontFamily: "'IBM Plex Serif', Georgia, serif" }}>Vtrender<sup>™</sup> Charts</div>
              <div className="text-[11px] walkthrough-header-subtitle" style={{ color: "#6b6960" }}>User manual & walkthrough</div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 walkthrough-header-actions">
          <a
            href={learningPathPdf}
            download
            className="inline-flex items-center gap-1.5 text-[12px] walkthrough-download-btn"
            style={{
              color: "#dce7ff",
              border: "1px solid rgba(122, 156, 255, 0.55)",
              background: "rgba(63, 105, 248, 0.08)",
              whiteSpace: "nowrap",
              cursor: "pointer",
              padding: "0.33rem 0.55rem",
              borderRadius: 6,
            }}
          >
            <DownloadOutlinedIcon style={{ fontSize: 16 }} />
            <span className="download-label-full">Download Learning Pathway</span>
            <span className="download-label-short">Download</span>
          </a>
          <button
            type="button"
            onClick={() => {
              if (onBack) onBack();
            }}
            className="inline-flex items-center gap-1.5 text-[12px]"
            style={{
              color: "#b4c3f4",
              border: "1px solid rgba(122, 156, 255, 0.55)",
              background: "rgba(63, 105, 248, 0.08)",
              cursor: "pointer",
              padding: "0.3rem 0.9rem",
              borderRadius: 6,
              fontSize: 16,
            }}
          >
            Close <CancelOutlinedIcon/>
          </button>
          {/* <CancelOutlinedIcon
                onClick={() => onBack()}
                sx={{
                ml: 0, mt: 0,top: `calc(7% - 5px)`,left: `calc(99% - 2px)`,
                 height: 24, width: 26, cursor: 'pointer', position: 'fixed', zIndex: 9999
                }}
            /> */}
        </div>
      </header>

      <div className="flex walkthrough-body" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <aside
          className={`${isSidebarOpen ? "block" : "hidden"} flex-shrink-0 overflow-y-auto walkthrough-sidebar`}
          style={{
            width: 252,
            background: "rgba(10, 18, 32, 0.86)",
            borderRight: "1px solid rgba(255,255,255,0.08)",
            padding: "0.8rem 0.28rem",
            height: "100%",
          }}
        >
          {SECTIONS.map((section) => (
            <div key={section.id} className="mb-5">
              <div className="px-4 mb-2 flex items-center gap-2" style={{ opacity: 0.95 }}>
                <span style={{ color: section.tier ? TIERS[section.tier].accent : "#8ea2c8", display: "inline-flex", alignItems: "center" }}>
                  {sectionIcon(section.id)}
                </span>
                {section.tier && <span className="inline-block w-1 h-1 rounded-full" style={{ background: TIERS[section.tier].dot }} />}
                <span className="text-[10px] uppercase tracking-widest" style={{ color: section.tier ? TIERS[section.tier].accent : "#6b6960" }}>
                  {section.label}
                </span>
              </div>
              {section.items.map((item) => {
                const isActive = activeId === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => onNav(item.id)}
                    className="w-full text-left py-2 text-[13px] transition-colors flex items-center gap-2 walkthrough-nav-item"
                    style={{
                      background: isActive ? "rgba(63, 105, 248, 0.20)" : "transparent",
                      color: isActive ? "#ffffff" : "#9aa7bf",
                      borderLeft: isActive ? "3px solid #3F69F8" : "3px solid transparent",
                      border: isActive ? "1px solid rgba(122, 156, 255, 0.55)" : "1px solid rgba(122, 156, 255, 0.18)",
                      borderRadius: "7px",
                      margin: "2px 8px",
                      paddingLeft: isActive ? 10 : 11,
                      paddingRight: 12,
                      fontWeight: isActive ? 500 : 400,
                    }}
                  >
                    <span style={{ display: "inline-flex", opacity: isActive ? 0.95 : 0.55 }}>
                      <ChevronRight size={12} />
                    </span>
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        <main
          ref={mainRef}
          className={`${!isDesktop && isSidebarOpen ? "hidden" : "block"} flex-1 overflow-y-auto walkthrough-main`}
          style={{ padding: "1rem 1rem 1.35rem", maxWidth: "100%" }}
        >
          <div
            className="walkthrough-main-card"
            style={{
              maxWidth: "100%",
              margin: "0",
              background: "linear-gradient(180deg, rgba(19,45,90,0.88) 0%, rgba(15,36,70,0.88) 100%)",
              border: "1px solid rgba(255,255,255,0.10)",
              borderRadius: 14,
              padding: "1.2rem 1.45rem 1.6rem",
              boxShadow: "0 14px 38px rgba(0, 0, 0, 0.32)",
            }}
          >
            {current
              ? (current.kind === "tool"
                  ? <ToolPage tool={current} onNav={onNav} />
                  : <SectionPage section={current} onNav={onNav} />)
              : <div style={{ color: "#8a8880" }}>Content coming in Round 2.</div>
            }
          </div>
        </main>
      </div>
    </div>
  );
}
